import { FC, FormEvent, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';
import BrandDialog from '../../components/BrandDialog';
import { ChipOption } from '../../components/MultiSelectChips';
import { ON_PRIMARY_ERROR_SX } from '../../components/fieldStyles';
import { archivosFetch } from '../../utils/fetch';
import { AuthUser } from '../../models/user';
import { HOME_USERS_KEY, fullName } from '../Home/useFilterOptions';
import { formatLastLogin } from '../UsersList/types';
import UserFormFields from './UserFormFields';
import { useUserFormOptions } from './useUserFormOptions';
import {
  EMPTY_USER_FORM,
  UserFormErrors,
  UserFormValues,
  formValuesFromUser,
  toPayload,
  validateUserForm,
} from './types';

// XD screen 9 ("9. Add user" in DESIGN.md): the blue "Ajouter un
// utilisateur" modal, with "Valider" (orange) and "Annuler". It opens over the
// users list when navigating to `/create-user` and closes returning to
// `/users-list` (see pages/UsersList).
//
// The same modal serves to modify (`mode="edit"`, route `/edit-user/:id`):
// it loads the account with `GET /api/users/:id`, and sends `PATCH /api/users/`.
// It replaces the former EditUser page. The XD does not draw the edit; the
// "Modifier un utilisateur" title follows the pattern of the create one.
//
// And also to view (`mode="view"`, route `/view-user/:id`, which the name in
// the list links to): the same fields read-only, without
// "Valider", with "Modifier" leading to `/edit-user/:id`. The XD has no
// "view user"; the modal is reused instead of designing a separate record.
// The title is the account name. If it does not exist, `onMissing` (the list
// redirects to /404, as the former ViewUser page did).
//
// "Mon profil" (`/profile`, pages/Profile) is this same modal in `view`
// over the own account: `title` renames it, `canEdit` removes "Modifier"
// from whoever cannot edit (`PATCH /users/` is `requireAdmin`, there is no
// editing of one's own profile for a USER) and `extraActions` adds "Changer
// le mot de passe". In read mode "Dernière connexion" is also shown.
//
// Behaviour kept from the previous form: email validation,
// `POST /api/users/`, success notice and empty form after creating. The
// "set password" email is sent by the backend on creation.
//
// Noted API gap: `POST /api/users/` creates the account and THEN sends
// the email; it only answers 200 if the email went out. If sending fails (on the
// development stack always: the transport points to an external SMTP host
// and nginx cuts off at 60 s with a 504), the account exists even though the response
// says otherwise. So as not to mislead, after a failure it checks by email
// whether the account ended up created, and in that case it warns that the email
// is missing instead of inviting a retry and creating a duplicate (the backend does
// not enforce email uniqueness either).

export type UserFormMode = 'create' | 'edit' | 'view';

export type UserFormNotice = {
  severity: 'success' | 'warning';
  text: string;
};

type Props = {
  open: boolean;
  mode: UserFormMode;
  /** Account to modify or view (`mode="edit"` / `"view"`). */
  userId?: string;
  onClose: () => void;
  onSaved?: (notice: UserFormNotice) => void;
  /** `mode="view"`: the account does not exist (404). */
  onMissing?: () => void;
  /** Title instead of the account name ("Mon profil"). */
  title?: string;
  /** `mode="view"`: offer "Modifier" (yes by default). */
  canEdit?: boolean;
  /** `mode="view"`: extra buttons between "Fermer" and "Modifier". */
  extraActions?: ReactNode;
};

const GENERIC_ERROR =
  "L'opération n'a pas pu aboutir. Veuillez réessayer dans quelques instants.";

// The backend answers in English; what the user can understand goes here.
const MESSAGES: Record<string, string> = {
  'User not found.': "Cet utilisateur n'existe plus.",
  'At least one service is required': 'Sélectionnez au moins un service.',
};

const translate = (message?: string): string =>
  (message && MESSAGES[message]) || GENERIC_ERROR;

const TITLES: Record<UserFormMode, string> = {
  create: 'Ajouter un utilisateur',
  edit: 'Modifier un utilisateur',
  view: 'Utilisateur',
};

const API = `${process.env.REACT_APP_API_URL}/api/users`;

const readMessage = async (response: Response): Promise<string | undefined> => {
  try {
    const body = await response.json();
    return typeof body?.message === 'string' ? body.message : undefined;
  } catch (_) {
    return undefined;
  }
};

// After a failed `POST`: did the account end up created? (see header note).
const findByEmail = async (email: string): Promise<boolean> => {
  try {
    const params = new URLSearchParams({
      page: '1',
      limit: '50',
      searchText: email,
    });
    const response = await archivosFetch(`${API}?${params.toString()}`);
    if (!response.ok) return false;
    const body = await response.json();
    const users: AuthUser[] = Array.isArray(body?.users) ? body.users : [];
    return users.some(
      (user) => user.email?.toLowerCase() === email.toLowerCase(),
    );
  } catch (_) {
    return false;
  }
};

const UserFormDialog: FC<Props> = ({
  open,
  mode,
  userId,
  onClose,
  onSaved,
  onMissing,
  title: titleOverride,
  canEdit = true,
  extraActions,
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isView = mode === 'view';
  // In a ref: the list defines it on each render, and it must not re-trigger the load.
  const onMissingRef = useRef(onMissing);
  onMissingRef.current = onMissing;
  const [values, setValues] = useState<UserFormValues>(EMPTY_USER_FORM);
  const [errors, setErrors] = useState<UserFormErrors>({});
  const [formError, setFormError] = useState<string>();
  const [loadState, setLoadState] = useState<'idle' | 'loading' | 'missing'>(
    'idle',
  );
  const [lastLoginAt, setLastLoginAt] = useState<string | null>(null);

  const { services, users, functions } = useUserFormOptions(open);

  // Loads the account to modify, every time the modal opens.
  useEffect(() => {
    if (!open) return;

    setErrors({});
    setFormError(undefined);

    if (mode === 'create' || !userId) {
      setValues(EMPTY_USER_FORM);
      setLoadState('idle');
      return;
    }

    let cancelled = false;
    setLoadState('loading');
    setValues(EMPTY_USER_FORM);

    archivosFetch(`${API}/${encodeURIComponent(userId)}`)
      .then(async (response) => {
        if (cancelled) return;
        if (response.status === 404) {
          setLoadState('missing');
          if (mode === 'view') onMissingRef.current?.();
          return;
        }
        if (!response.ok) {
          setFormError(GENERIC_ERROR);
          setLoadState('missing');
          return;
        }
        const body = await response.json();
        if (cancelled) return;
        setValues(formValuesFromUser(body.user));
        setLastLoginAt(
          typeof body.user?.last_login_at === 'string' ? body.user.last_login_at : null,
        );
        setLoadState('idle');
      })
      .catch(() => {
        if (!cancelled) {
          setFormError(GENERIC_ERROR);
          setLoadState('missing');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, mode, userId]);

  const { mutateAsync: save, isLoading: isSaving } = useMutation(
    (payload: ReturnType<typeof toPayload>) =>
      archivosFetch(`${API}/`, {
        method: mode === 'create' ? 'POST' : 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }),
  );

  const serviceOptions = useMemo<ChipOption[]>(
    () =>
      services
        .map((service) => ({ value: service.id, label: service.name }))
        .sort((a, b) => a.label.localeCompare(b.label, 'fr')),
    [services],
  );

  // "Pour le compte de": any other account. The one being edited cannot
  // be delegated to itself.
  const userOptions = useMemo<ChipOption[]>(
    () =>
      users
        .filter((user) => user.id && user.id !== userId)
        .map((user) => ({ value: user.id as string, label: fullName(user) }))
        .sort((a, b) => a.label.localeCompare(b.label, 'fr')),
    [users, userId],
  );

  // "Fonction" stays free text. Offered: the functions recorded at /functions,
  // and the ones already typed on an account, so a function entered once is
  // suggested on the next form. The users list is reloaded on every save
  // (see `finish`), so a function just typed is there straight away. Two
  // spellings differing only by case are offered once.
  const functionSuggestions = useMemo(() => {
    const byKey = new Map<string, string>();

    [
      ...functions.map((entity) => entity.name),
      ...users.map((user) => user.function ?? ''),
    ].forEach((name) => {
      const trimmed = name.trim();
      const key = trimmed.toLocaleLowerCase('fr');
      if (trimmed && !byKey.has(key)) byKey.set(key, trimmed);
    });

    return Array.from(byKey.values()).sort((a, b) => a.localeCompare(b, 'fr'));
  }, [functions, users]);

  const update = (patch: Partial<UserFormValues>) => {
    setValues((current) => ({ ...current, ...patch }));
    // A field's error is removed as soon as it is touched again.
    const touched = Object.keys(patch) as Array<keyof UserFormValues>;
    setErrors((current) => {
      const next = { ...current };
      touched.forEach((key) => {
        if (key in next) delete next[key as keyof UserFormErrors];
      });
      return next;
    });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isView || isSaving || loadState !== 'idle') return;

    const found = validateUserForm(values);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      return;
    }

    setFormError(undefined);
    const payload = toPayload(values, mode === 'edit' ? userId : undefined);

    try {
      const response = await save(payload);

      if (response.ok) {
        finish({
          severity: 'success',
          text:
            mode === 'create'
              ? 'Utilisateur créé avec succès.'
              : 'Utilisateur modifié avec succès.',
        });
        return;
      }

      if (mode === 'create' && (await findByEmail(payload.email))) {
        finish({
          severity: 'warning',
          text: "Utilisateur créé, mais l'email de définition du mot de passe n'a pas pu être envoyé.",
        });
        return;
      }

      setFormError(translate(await readMessage(response)));
    } catch (_) {
      setFormError(GENERIC_ERROR);
    }
  };

  const finish = (notice: UserFormNotice) => {
    setValues(EMPTY_USER_FORM);
    setErrors({});
    // The "Pour le compte de" list and the Home's "Archiveur" dropdown
    // share this cache: a new or renamed account must be visible.
    queryClient.invalidateQueries([HOME_USERS_KEY]);
    onSaved?.(notice);
  };

  const busy = isSaving;
  const canSubmit = loadState === 'idle';

  const title =
    titleOverride ??
    (isView
      ? (loadState === 'idle' &&
          fullName({
            firstname: values.firstname,
            lastname: values.lastname,
            email: values.email,
          })) ||
        TITLES.view
      : TITLES[mode]);

  return (
    <BrandDialog
      open={open}
      title={title}
      onClose={onClose}
      onSubmit={isView ? undefined : handleSubmit}
      busy={busy}
      // Wide enough for the two field columns without clipping the
      // right-hand dropdown placeholders.
      maxWidth="lg"
      actions={
        <>
          <Button
            variant="outlined"
            color="inherit"
            onClick={onClose}
            disabled={busy}
          >
            {isView ? 'Fermer' : 'Annuler'}
          </Button>
          {isView && canSubmit && extraActions}
          {isView && canSubmit && userId && canEdit && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => navigate(`/edit-user/${userId}`)}
            >
              Modifier
            </Button>
          )}
          {!isView && canSubmit && (
            <Button
              type="submit"
              variant="contained"
              color="secondary"
              disabled={busy}
              startIcon={
                busy ? <CircularProgress size={16} color="inherit" /> : undefined
              }
            >
              Valider
            </Button>
          )}
        </>
      }
    >
      {formError && (
        <Typography role="alert" sx={{ ...ON_PRIMARY_ERROR_SX, mb: 2, fontSize: '0.875rem' }}>
          {formError}
        </Typography>
      )}

      {loadState === 'loading' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress color="inherit" />
        </Box>
      )}

      {loadState === 'missing' && !formError && (
        <Typography role="alert" sx={{ py: 4, textAlign: 'center' }}>
          Cet utilisateur n'existe plus.
        </Typography>
      )}

      {loadState === 'idle' && (
        <UserFormFields
          values={values}
          errors={errors}
          onChange={update}
          serviceOptions={serviceOptions}
          userOptions={userOptions}
          functionSuggestions={functionSuggestions}
          disabled={busy}
          readOnly={isView}
          lastLogin={isView ? formatLastLogin(lastLoginAt) : undefined}
        />
      )}
    </BrandDialog>
  );
};

export default UserFormDialog;
